import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, EnvelopeSimple, MapPin, Warning } from "@phosphor-icons/react";

// AuthoritiesTab — admin panel to configure regional government / municipal
// contacts. When an observation of kind "road" or "event" is approved for a
// matching region, an authority_alert row is queued (Resend/SMTP delivery is
// a plug-in later).

const REGION_TYPES = ["country", "state", "county", "city", "village"];
const SCOPES = ["any", "road", "environment", "wildlife"];

export default function AuthoritiesTab() {
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", region_type: "city", region_value: "", scope: "road", notes: "" });

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        http.get("/nature/authorities"),
        http.get("/nature/authority-alerts?status=queued"),
      ]);
      setItems(a.data?.authorities || []);
      setAlerts(b.data?.alerts || []);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.email || !form.region_value) { toast.info("Name, email and region are required"); return; }
    setBusy(true);
    try {
      await http.post("/nature/authorities", form);
      setForm((f) => ({ ...f, name: "", email: "", region_value: "", notes: "" }));
      toast.success("Authority added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate this authority?")) return;
    try { await http.delete(`/nature/authorities/${id}`); load(); toast.success("Deactivated"); }
    catch { toast.error("Failed"); }
  };

  const markSent = async (id) => {
    try { await http.post(`/nature/authority-alerts/${id}/status`, { status: "sent" }); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="authorities-tab">
      {/* Add form */}
      <div className="rounded-xl bg-white border border-black/15 p-5 text-[#1B4332]">
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#E4A15C]">/ Add authority contact</div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-6 gap-2">
          <input placeholder="Name (e.g. Kottayam Municipal Corp.)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="md:col-span-2 bg-transparent border border-black/15 rounded-lg px-3 py-2 text-sm placeholder:text-[#3A3830]" data-testid="auth-name" />
          <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="md:col-span-2 bg-transparent border border-black/15 rounded-lg px-3 py-2 text-sm placeholder:text-[#3A3830]" data-testid="auth-email" />
          <select value={form.region_type} onChange={(e) => setForm({ ...form, region_type: e.target.value })} className="bg-transparent border border-black/15 rounded-lg px-2 py-2 text-sm" data-testid="auth-region-type">
            {REGION_TYPES.map((t) => <option key={t} value={t} className="text-[#1B4332]">{t}</option>)}
          </select>
          <input placeholder="Region value (e.g. Kottayam)" value={form.region_value} onChange={(e) => setForm({ ...form, region_value: e.target.value })} className="bg-transparent border border-black/15 rounded-lg px-3 py-2 text-sm placeholder:text-[#3A3830]" data-testid="auth-region-value" />
          <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="bg-transparent border border-black/15 rounded-lg px-2 py-2 text-sm" data-testid="auth-scope">
            {SCOPES.map((s) => <option key={s} value={s} className="text-[#1B4332]">{s}</option>)}
          </select>
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="md:col-span-4 bg-transparent border border-black/15 rounded-lg px-3 py-2 text-sm placeholder:text-[#3A3830]" data-testid="auth-notes" />
          <button disabled={busy} onClick={add} className="md:col-span-1 rounded-lg bg-[#E4A15C] text-[#1B4332] px-3 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50" data-testid="auth-add">
            <Plus size={12} weight="bold" className="inline mr-1" /> Add
          </button>
        </div>
      </div>

      {/* Existing */}
      <div>
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#E4A15C] mb-2">/ Configured ({items.length})</div>
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-black/15 p-6 text-[#3A3830] text-sm">No authorities configured yet. Add one above.</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {items.map((a) => (
              <li key={a.id} className="rounded-lg bg-black/5 border border-black/15 p-3 text-[#1B4332]" data-testid={`auth-${a.id}`}>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-serif-display text-base leading-tight">{a.name}</div>
                    <div className="mt-0.5 text-xs text-[#3A3830] flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1"><EnvelopeSimple size={11} /> {a.email}</span>
                      <span className="inline-flex items-center gap-1"><MapPin size={11} /> {a.region_type}: {a.region_value}</span>
                      <span className="rounded-full bg-black/5 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">{a.scope}</span>
                    </div>
                  </div>
                  <button onClick={() => remove(a.id)} data-testid={`auth-remove-${a.id}`} className="text-rose-300 hover:text-rose-100 p-1"><Trash size={13} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Queued alerts */}
      <div>
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#E4A15C] mb-2">/ Queued alerts ({alerts.length})</div>
        {alerts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-black/15 p-6 text-[#3A3830] text-sm">Nothing queued right now.</div>
        ) : (
          <ul className="space-y-2">
            {alerts.map((al) => (
              <li key={al.id} className="rounded-lg bg-black/5 border border-black/15 p-3 text-[#1B4332]" data-testid={`alert-${al.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#E4A15C]">To {al.authority_name} · {al.authority_email}</div>
                    <div className="font-serif-display text-base leading-tight mt-1">{al.subject}</div>
                    <pre className="mt-1 whitespace-pre-wrap text-xs text-[#3A3830] font-sans">{al.body}</pre>
                  </div>
                  <button onClick={() => markSent(al.id)} data-testid={`alert-sent-${al.id}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-200 px-3 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-emerald-500/20">
                    Mark sent
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-2 flex items-start gap-2 text-xs text-[#3A3830]">
          <Warning size={14} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
          <div>Email dispatch is not yet wired — alerts are queued here so you can review, refine and dispatch via your preferred provider (Resend recommended).</div>
        </div>
      </div>
    </div>
  );
}
